import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import Cropper from 'react-easy-crop';
import type { Area, Point } from 'react-easy-crop';
import './ImageCropModal.scss';

async function getCroppedBlob(imageSrc: string, pixelCrop: Area, mimeType: string): Promise<Blob> {
  const image = new Image();
  image.src = imageSrc;
  await new Promise<void>((resolve, reject) => {
    image.onload = () => resolve();
    image.onerror = () => reject(new Error('Failed to load image'));
  });

  const canvas = document.createElement('canvas');
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not get canvas context');

  ctx.drawImage(
    image,
    pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height,
    0, 0, pixelCrop.width, pixelCrop.height,
  );

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => { blob ? resolve(blob) : reject(new Error('Canvas is empty')); },
      mimeType,
      0.92,
    );
  });
}

export interface ImageCropModalProps {
  open: boolean;
  imageSrc: string;
  /** e.g. 1 for square logo, 16/9 for banner. Omit for free-form. */
  aspect?: number;
  onCrop: (file: File, previewUrl: string) => void;
  onCancel: () => void;
  fileName?: string;
  mimeType?: string;
}

function ImageCropModal({
  open,
  imageSrc,
  aspect,
  onCrop,
  onCancel,
  fileName = 'image',
  mimeType = 'image/jpeg',
}: ImageCropModalProps) {
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);

  useEffect(() => {
    if (open) {
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
    }
  }, [open, imageSrc]);

  const onCropComplete = useCallback((_croppedArea: Area, croppedPixels: Area) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const handleConfirm = async () => {
    if (!croppedAreaPixels) return;
    const blob = await getCroppedBlob(imageSrc, croppedAreaPixels, mimeType);
    const ext = mimeType.split('/')[1]?.replace(/[+;].*$/, '') || 'jpg';
    const baseName = fileName.replace(/\.[^.]+$/, '');
    const file = new File([blob], `${baseName}_cropped.${ext}`, { type: mimeType });
    const previewUrl = URL.createObjectURL(blob);
    onCrop(file, previewUrl);
  };

  if (!open) return null;

  return createPortal(
    <div className="img-crop-root" role="dialog" aria-modal="true" aria-label="Crop image">
      <div className="img-crop-overlay" onClick={onCancel} />
      <div
        className="img-crop-panel"
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="img-crop-header">
          <span className="img-crop-title">Crop Image</span>
          <button type="button" className="img-crop-close" onClick={onCancel} aria-label="Cancel">
            ×
          </button>
        </div>
        <div className="img-crop-area">
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={aspect}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
          />
        </div>
        <div className="img-crop-controls">
          <div className="img-crop-zoom-row">
            <span className="img-crop-zoom-label">Zoom</span>
            <input
              type="range"
              min={1}
              max={3}
              step={0.01}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              className="img-crop-zoom-slider"
            />
          </div>
          <div className="img-crop-actions">
            <button type="button" className="img-crop-btn-cancel" onClick={onCancel}>
              Cancel
            </button>
            <button type="button" className="img-crop-btn-confirm" onClick={handleConfirm}>
              Crop &amp; Use
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default ImageCropModal;
